// @ts-strict-ignore
import dayjs from 'dayjs';
import type { Option } from '@usealloy/component-library';
import type { WorkflowChange, WhatIfPreReqs } from './types';

export const sampleTypes = {
  random: 'random',
  specific: 'specific',
} as const;

export type WorkflowTestingSuitePageNames = (
  'previewChanges'
  | 'saveVersion'
  | 'selectDataType'
  | 'selectVersion'
  | 'testConfiguration'
  | 'confirmation'
  | 'results'
);

export type RealtimeBacktestFilterParams = {
  created_at?: {
    from: string;
    to: string;
  };
  application_tokens?: {
    [token: string]: boolean;
  };
  application_public_versions?: {
    [version: string]: boolean;
  };
  customer_test_tokens?: {
    [testToken: string]: boolean;
  };
  evaluation_tokens?: string[];
  limit?: number;
};

export interface WorkflowTestingSuiteState {
  championChallenger: {
    isModalOpen: boolean;
    willCreateNewWorkflowVersion?: boolean;
    /**
     * Used when Champion Challenger is selected to run on a new workflow version
     * made with unsaved changes from the workflow editor.
     * */
    displayVersion?: number;
  };
  // ----------------------------------------------------------------------------------------------
  shared: {
    currentPage: WorkflowTestingSuitePageNames;
    /** Tied to test type, ie: what-if or champion challenger. */
    dataType?: 'cached' | 'sandbox' | 'uploaded' | 'live' | 'pe-backtest' | 'events-backtest' | 'evaluations-backtest' | 'agent-backtest';
    isVisible: boolean;
    /** Editable test names are supported for some test types, i.e events-backtest */
    testName?: string;
    /** Editable test notes are supported for some test types, i.e events-backtest */
    testNotes?: string;
    /** Used in what-if, but also determines the first page of Testing Suite. */
    workflowChanges: WorkflowChange[];
    /** The version of the workflow to run test data through */
    versionToTest?: number;
    /**
     * The version whose data is to be used in the tests.
     * This applies to testing changes to a workflow as well as testing one
     * version of a workflow against another.
     */
    versionToTestAgainst?: number;
    /** A.k.a applicationToken, an identifier for a workflow */
    workflowActivePublicVersion?: number;
    /** The active public version number of the workflow */
    workflowToken?: string;
    /** The type of workflow, e.g. 'person', 'business', 'transaction', 'portfolio' */
    workflowType?: string;
    /** Name of the workflow */
    workflowName?: string;
    /** Version Notes if there are workflowChanges that need to be saved as a new version */
    versionNotes?: string;
    /** Flag to indicate if a test is currently being submitted */
    isSubmittingTest?: boolean;
  };
  // ----------------------------------------------------------------------------------------------
  whatIf: {
    analysisToken?: string;
    errors?: {
      createdAt?: { message: string };
      evaluationTokens: boolean;
    };
    filters: {
      createdAt?: string[];
      filteredEvaluationsSampleSize?: number;
      fraudScore?: string[];
      outcome?: Option[];
      reasonCode?: string[];
      reviewReason?: string[];
      sampleSize?: number;
      sampleType?: keyof typeof sampleTypes;
      tags?: Option[];
      // comma-separated string of evaluation tokens
      evaluationTokens?: string;
    };
    isFetching: boolean;
    /**
     * While this can be fetched in a component, the fetch is made prior to the
     * UI opening, so might as well save an extra network request.
     */
    isHistoricalDataAvailable?: {
      baseVersion?: boolean;
      otherVersions?: boolean;
    };
    newEvalsModal: {
      confirmed: boolean;
      open: boolean;
    };
    willCreateNewEvals?: boolean;
    prerequisites: WhatIfPreReqs;
  };
  // ------------------------------------PORTFOLIO EVALUATION BACKTESTS------------------------------------------
  peBacktest: {
    /** Determines the reference time the Workflow uses for evaluation */
    asOfTime?: string;
    /** PE tokens */
    testSample?: string[];
    sampleSize?: number;
  };
  // -----------------------------------------REALTIME BACKTESTS-------------------------------------------------
  rtBacktest: {
    /** Filters applied to return the test sample */
    filterParams?: RealtimeBacktestFilterParams;
    /** Whether any evaluation tokens in the token input are misformatted */
    hasTokenFormatError: boolean;
    /** The actual size of the test sample */
    sampleSize: number;
    testName?: string;
    testNotes?: string;
    /** A previous realtime test token */
    testToken?: string[];
    /** The total size of the sampleset available */
    totalSize: number;
  };
  // -----------------------------------------AGENT BACKTESTS------------------------------------------------
  agentBacktest: {
    /** Which agent to run the backtest against. */
    agentKey?: string;
    /** Historical window the cohort is drawn from (ISO strings). `end` optional (defaults to now). */
    timeWindow: {
      start?: string;
      end?: string;
    };
    /** Draw the cohort from sandbox applications (e.g. local/dev testing). */
    sandbox: boolean;
    /** Max number of applications to run the agent against. */
    limit?: number;
  };
}

export const initialState: WorkflowTestingSuiteState = {
  championChallenger: {
    isModalOpen: false,
    willCreateNewWorkflowVersion: null,
  },
  shared: {
    currentPage: 'previewChanges',
    dataType: null,
    isVisible: false,
    testName: '',
    versionToTestAgainst: null,
    workflowChanges: [],
    versionNotes: '',
    isSubmittingTest: false,
  },
  whatIf: {
    errors: {
      createdAt: undefined,
      evaluationTokens: false,
    },
    filters: {
      createdAt: [dayjs().subtract(1, 'years').toISOString(), dayjs().toISOString()],
      filteredEvaluationsSampleSize: 0,
      fraudScore: null,
      outcome: null,
      reasonCode: null,
      reviewReason: null,
      sampleSize: 500,
      sampleType: sampleTypes.random,
      tags: null,
      evaluationTokens: null,
    },
    isFetching: false,
    newEvalsModal: {
      confirmed: false,
      open: false,
    },
    willCreateNewEvals: null,
    prerequisites: {
      sufficientEvaluationsApplicationBase: false,
    },
  },
  peBacktest: {
    asOfTime: null,
    testSample: null,
    sampleSize: null,
  },
  rtBacktest: {
    filterParams: null,
    hasTokenFormatError: false,
    sampleSize: null,
    testName: null,
    testNotes: null,
    testToken: null,
    totalSize: null,
  },
  agentBacktest: {
    // 'kyc-agent' is currently the only wired agent; hardcoded until more agents are backtestable.
    agentKey: 'kyc-agent',
    timeWindow: {
      start: dayjs().subtract(1, 'years').toISOString(),
      end: undefined,
    },
    sandbox: false,
    limit: 25,
  },
};
